import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageHero } from "@/components/PageHero";
import { SmartLink } from "@/components/SmartLink";
import { PAGES } from "@/lib/pages";
import { useVisitor } from "@/lib/visitor";
import { PastoralForm } from "@/components/PastoralForm";
import { AutoRail } from "@/components/AutoRail";
import { useDevotionalsOn, keepLink } from "@/lib/display";

const InfoCard = ({ card, i, onAction, testId }) => {
  const Icon = card.icon;
  const btnCls = "mt-5 rounded-full bg-[#0A3A6B] px-5 hover:bg-[#1D64B4]";
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-shadow hover:shadow-xl" data-testid={testId}>
      {card.img && <img src={card.img} alt={card.title} className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      <div className="flex flex-1 flex-col p-6 sm:p-7">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EBF3FA] text-[#1D64B4]"><Icon className="h-5 w-5" /></span>
        <h3 className="mt-4 text-xl font-semibold text-slate-900">{card.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{card.text}</p>
        {card.pastoral ? (
          <Button onClick={() => onAction("__pastoral", card.pastoral)} className={`${btnCls} w-fit`} data-testid={`${testId}-button`}>{card.cta} <ArrowRight className="ml-1 h-4 w-4" /></Button>
        ) : card.action ? (
          <Button onClick={() => onAction(card.action, card.preset)} className={`${btnCls} w-fit`} data-testid={`${testId}-button`}>{card.cta} <ArrowRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button asChild className={`${btnCls} w-fit`} data-testid={`${testId}-button`}><SmartLink href={card.href}>{card.cta} <ArrowRight className="ml-1 h-4 w-4" /></SmartLink></Button>
        )}
      </div>
    </motion.div>
  );
};

export default function InfoPage({ pageKey }) {
  const page = PAGES[pageKey];
  const { openAction } = useVisitor();
  const devOn = useDevotionalsOn();
  const cards = page.cards.filter(keepLink(devOn));
  const [pastoral, setPastoral] = useState(null);
  const onAction = (a, preset) => (a === "__pastoral" ? setPastoral(preset) : openAction(a, preset));
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`page-${pageKey}`}>
      <SiteHeader />
      <PageHero eyebrow={page.eyebrow} title={page.title} lead={page.lead} img={page.img} />
      <main className="mx-auto max-w-7xl px-5 py-14 lg:px-10 lg:py-20">
        {page.note && <p className="mb-8 rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-500">{page.note}</p>}
        {pageKey === "pastoral-care" ? (
          <AutoRail section="pastoral" items={page.cards} render={(c, i) => <InfoCard key={c.title} card={c} i={i} onAction={onAction} testId={`info-card-${i}`} />} />
        ) : (
          <div className={`grid gap-5 sm:grid-cols-2 ${cards.length % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-2 xl:grid-cols-4"}`}>
            {cards.map((c, i) => <InfoCard key={c.title} card={c} i={i} onAction={onAction} testId={`info-card-${i}`} />)}
          </div>
        )}
        <PastoralForm service={pastoral} onClose={() => setPastoral(null)} />
      </main>
      <SiteFooter />
    </div>
  );
}
